import { useEffect } from 'react';

const ITEM_SELECTOR = '.crm-nav-dock-flat-item';

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function isCoarsePointer() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/**
 * Magnificación continua estilo dock de macOS: al mover el cursor sobre el dock,
 * cada ítem escala según su distancia al puntero (efecto "fisheye" con onda suave
 * sobre los vecinos). Escribe la escala en la variable CSS `--dock-mag` por ítem.
 *
 * Solo se activa en punteros finos (no touch) y respeta `prefers-reduced-motion`.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - contenedor del dock (la fila de ítems).
 * @param {object} [opts]
 * @param {boolean} [opts.enabled=true]
 * @param {number} [opts.maxScale=1.55] - escala máxima del ítem justo bajo el cursor.
 * @param {number} [opts.influence=120] - radio (px) de influencia de la onda.
 */
export function useDockMagnify(containerRef, { enabled = true, maxScale = 1.55, influence = 120 } = {}) {
  useEffect(() => {
    const container = containerRef?.current;
    if (!enabled || !container) return undefined;
    if (prefersReducedMotion() || isCoarsePointer()) return undefined;

    let raf = 0;
    let pointerX = null;

    const reset = (items) => {
      items.forEach((item) => item.style.setProperty('--dock-mag', '1'));
    };

    const apply = () => {
      raf = 0;
      const items = container.querySelectorAll(ITEM_SELECTOR);
      if (!items.length) return;
      if (pointerX === null) {
        reset(items);
        return;
      }
      items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.width === 0) {
          item.style.setProperty('--dock-mag', '1');
          return;
        }
        const center = rect.left + rect.width / 2;
        const dist = Math.abs(pointerX - center);
        let scale = 1;
        if (dist < influence) {
          // Onda coseno: 1 en el centro -> 0 en el borde de influencia (transición suave).
          const t = dist / influence;
          const falloff = 0.5 * (1 + Math.cos(t * Math.PI));
          scale = 1 + (maxScale - 1) * falloff;
        }
        item.style.setProperty('--dock-mag', scale.toFixed(3));
      });
    };

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(apply);
    };

    const onMove = (event) => {
      pointerX = event.clientX;
      schedule();
    };

    const onLeave = () => {
      pointerX = null;
      schedule();
    };

    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);

    return () => {
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
      if (raf) window.cancelAnimationFrame(raf);
      container
        .querySelectorAll(ITEM_SELECTOR)
        .forEach((item) => item.style.removeProperty('--dock-mag'));
    };
  }, [containerRef, enabled, maxScale, influence]);
}

export default useDockMagnify;
